import { useEffect, useState } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

/* ================= CONSTANTS ================= */

const STATUSES = ["TODO", "IN_PROGRESS", "DONE"];
const SPRINT_DURATION_DAYS = 15;
const daysBetween = (start, end) =>
  Math.floor((end - start) / (1000 * 60 * 60 * 24));

/* ================= COMPONENT ================= */

export default function SprintBoard() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [allSprints, setAllSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isSprintCompleted, setIsSprintCompleted] = useState(false);

  /* ================= LOAD USER SPRINTS ================= */

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setLoading(true);

      const ref = doc(db, "sprints", user.uid);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setLoading(false);
        return;
      }

      const sprintData = snap.data().sprints || [];
      const normalizedSprints = sprintData.map((s) => ({
        ...s,
        startedAt: s.startedAt || serverTimestamp(),
      }));

      setAllSprints(normalizedSprints);
      setActiveSprint(normalizedSprints[0] || null);
      await updateDoc(doc(db, "sprints", user.uid), {
        sprints: normalizedSprints,
      });
      setLoading(false);
    };

    load();
  }, [user]);

  /* ================= LOAD TASKS FOR ACTIVE SPRINT ================= */

  useEffect(() => {
    if (!activeSprint) return;

    setTasks(
      (activeSprint.tasks || []).map((t, i) => ({
        ...t,
        status: t.status || "TODO",
        sequence: i + 1,
      }))
    );

    setIsSprintCompleted(activeSprint.completed || false);
  }, [activeSprint]);
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.status === "DONE").length;

  const progressPercent =
    totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  const sprintStartedAt = activeSprint?.startedAt?.toDate
    ? activeSprint.startedAt.toDate()
    : new Date();

  const sprintDay =
    daysBetween(
      new Date(
        sprintStartedAt.getFullYear(),
        sprintStartedAt.getMonth(),
        sprintStartedAt.getDate()
      ),
      new Date()
    ) + 1;

  const remainingDays = Math.max(0, SPRINT_DURATION_DAYS - sprintDay);

  /* ================= SAVE TASK STATUS ================= */

  const persistTask = async (taskId, status) => {
    const updatedSprints = allSprints.map((s) => {
      if (s.sprintId !== activeSprint.sprintId) return s;

      return {
        ...s,
        tasks: s.tasks.map((t) => (t.id === taskId ? { ...t, status } : t)),
      };
    });

    setAllSprints(updatedSprints);
    setActiveSprint(
      updatedSprints.find((s) => s.sprintId === activeSprint.sprintId)
    );

    await updateDoc(doc(db, "sprints", user.uid), {
      sprints: updatedSprints,
      updatedAt: serverTimestamp(),
    });
  };

  /* ================= DRAG ================= */

  const onDragEnd = async (res) => {
    if (!res.destination || isSprintCompleted) return;

    const status = res.destination.droppableId;
    const taskId = res.draggableId;

    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status } : t))
    );

    await persistTask(taskId, status);
  };

  /* ================= COMPLETE SPRINT ================= */

  const markSprintComplete = async () => {
    if (!tasks.every((t) => t.status === "DONE")) return;

    const updatedSprints = allSprints.map((s) =>
      s.sprintId === activeSprint.sprintId ? { ...s, completed: true } : s
    );

    setAllSprints(updatedSprints);
    setActiveSprint({ ...activeSprint, completed: true });
    setIsSprintCompleted(true);

    await updateDoc(doc(db, "sprints", user.uid), {
      sprints: updatedSprints,
    });
  };

  /* ================= RENDER ================= */

  if (loading) return <Box>Loading Sprint Board…</Box>;
  if (!activeSprint) return <Box>No sprint found</Box>;

  return (
    <Container>
      <Layout>
        {/* LEFT SIDEBAR */}
        <Sidebar>
          <h3>📋 Sprints</h3>

          {allSprints.map((s) => (
            <SprintItem
              key={s.sprintId}
              active={s.sprintId === activeSprint.sprintId}
              onClick={() => setActiveSprint(s)}
            >
              <strong>{s.name}</strong>
              <small>Sprint {s.order}</small>
              {s.completed && <Completed>✓ Done</Completed>}
            </SprintItem>
          ))}
        </Sidebar>

        {/* RIGHT BOARD */}
        <Main>
          <Header>
            <HeaderLeft>
              <h2>{activeSprint.name}</h2>
              <p>{activeSprint.goal}</p>

              <MetaRow>
                <MetaItem>
                  🗓 Day <strong>{sprintDay}</strong> / {SPRINT_DURATION_DAYS}
                </MetaItem>

                <MetaItem>⏳ {remainingDays} days left</MetaItem>

                <MetaItem>
                  📋 {completedTasks}/{totalTasks} tasks
                </MetaItem>

                <MetaItem>📊 {progressPercent}% complete</MetaItem>
              </MetaRow>

              <ProgressBar>
                <ProgressFill style={{ width: `${progressPercent}%` }} />
              </ProgressBar>
            </HeaderLeft>

            <PrimaryBtn
              disabled={!tasks.every((t) => t.status === "DONE")}
              onClick={markSprintComplete}
            >
              {isSprintCompleted ? "Sprint Completed" : "Complete Sprint"}
            </PrimaryBtn>
          </Header>

          <DragDropContext onDragEnd={onDragEnd}>
            <Board>
              {STATUSES.map((status) => (
                <Droppable droppableId={status} key={status}>
                  {(p) => (
                    <Column ref={p.innerRef} {...p.droppableProps}>
                      <ColumnHeader>{status.replace("_", " ")}</ColumnHeader>

                      {tasks
                        .filter((t) => t.status === status)
                        .map((task, i) => (
                          <Draggable
                            key={task.id}
                            draggableId={task.id}
                            index={i}
                          >
                            {(p) => (
                              <Card
                                ref={p.innerRef}
                                {...p.draggableProps}
                                {...p.dragHandleProps}
                              >
                                <strong>
                                  {task.sequence}. {task.title}
                                </strong>
                              </Card>
                            )}
                          </Draggable>
                        ))}

                      {p.placeholder}
                    </Column>
                  )}
                </Droppable>
              ))}
            </Board>
          </DragDropContext>
        </Main>
      </Layout>
    </Container>
  );
}

/* ================= STYLES ================= */
const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  font-size: 12px;
  color: #c7d2fe;
  background: rgba(99, 102, 241, 0.15);
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
`;

const ProgressBar = styled.div`
  margin-top: 10px;
  height: 6px;
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  transition: width 0.3s ease;
`;

const Container = styled.div`
  min-height: 100vh;
  padding: 24px;
  background: #020617;
  color: #fff;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 20px;
`;

const Sidebar = styled.div`
  background: rgba(15, 23, 42, 0.9);
  border-radius: 16px;
  padding: 16px;
`;

const SprintItem = styled.div`
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);

  background: ${({ active }) =>
    active ? "rgba(99,102,241,0.25)" : "transparent"};

  &:hover {
    background: rgba(99, 102, 241, 0.18);
  }

  small {
    display: block;
    color: #9ca3af;
    font-size: 12px;
  }
`;

const Completed = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #22c55e;
  font-weight: 700;
`;

const Main = styled.div`
  background: rgba(15, 23, 42, 0.6);
  border-radius: 16px;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;

  p {
    color: #9ca3af;
    margin-top: 6px;
  }
`;

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const Column = styled.div`
  background: rgba(2, 6, 23, 0.9);
  padding: 12px;
  border-radius: 14px;
`;

const ColumnHeader = styled.h4`
  margin-bottom: 10px;
  color: #e5e7eb;
`;

const Card = styled.div`
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
`;

const PrimaryBtn = styled.button`
  padding: 10px 14px;
  border-radius: 12px;
  border: none;
  height: 50px;
  font-weight: 700;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Box = styled.div`
  padding: 40px;
`;
